import { useState } from "react";
import { Link } from "react-router-dom";
import { MessageSquareText, Menu, X, ArrowRight } from "lucide-react";

const PRODUCT_LINKS = [
  ["/solutions", "Solutions"],
  ["/compare", "Compare"],
  ["/resources", "Guides"],
  ["/#copilot", "Features"],
  ["/security", "Security"],
  ["/faq", "FAQ"],
];


export function MarketingNav() {
  const [open, setOpen] = useState(false);
  return (
    <nav className="sticky top-0 z-50 bg-sand-50/95 backdrop-blur border-b border-sand-200" data-testid="marketing-nav">
      <div className="max-w-6xl mx-auto px-5 sm:px-6 h-16 flex items-center justify-between gap-4">
        <Link to="/" className="flex items-center gap-2.5 shrink-0" data-testid="marketing-nav-logo">
          <div className="h-9 w-9 rounded-xl bg-redline-500 flex items-center justify-center shrink-0">
            <MessageSquareText className="h-5 w-5 text-white" strokeWidth={1.5} />
          </div>
          <div className="font-heading font-extrabold tracking-tight text-ink text-[17px]">Ask Redline®</div>
        </Link>

        <div className="hidden lg:flex items-center gap-6 text-sm font-semibold text-ink-700">
          {PRODUCT_LINKS.map(([to, label]) =>
            to.startsWith("/#")
              ? <a key={to} href={to} className="hover:text-redline-600 transition-colors">{label}</a>
              : <Link key={to} to={to} className="hover:text-redline-600 transition-colors" data-testid={`marketing-nav-${label.toLowerCase()}`}>{label}</Link>
          )}
        </div>

        <div className="hidden lg:flex items-center gap-4 shrink-0">
          <Link to="/login" className="text-sm font-semibold text-ink-500 hover:text-ink transition-colors">Sign in</Link>
          <a href="/#contact" data-testid="marketing-nav-book-demo" className="rounded-full bg-redline-500 text-white text-sm font-semibold px-5 py-2.5 hover:bg-redline-600 transition-colors">Book a demo</a>
        </div>

        <button data-testid="marketing-nav-toggle" onClick={() => setOpen((o) => !o)} aria-label="Menu"
          className="lg:hidden -mr-1.5 h-10 w-10 grid place-items-center rounded-lg text-ink hover:bg-sand-100 transition-colors">
          {open ? <X className="h-6 w-6" strokeWidth={2} /> : <Menu className="h-6 w-6" strokeWidth={2} />}
        </button>
      </div>
      {open && (
        <div className="lg:hidden border-t border-sand-200 bg-sand-50" data-testid="marketing-nav-mobile">
          <div className="px-5 sm:px-6 py-3 flex flex-col">
            {PRODUCT_LINKS.map(([to, label]) =>
              to.startsWith("/#")
                ? <a key={to} href={to} onClick={() => setOpen(false)} className="py-3 text-[15px] font-semibold text-ink-800 hover:text-redline-600 transition-colors border-b border-sand-200/70">{label}</a>
                : <Link key={to} to={to} onClick={() => setOpen(false)} className="py-3 text-[15px] font-semibold text-ink-800 hover:text-redline-600 transition-colors border-b border-sand-200/70">{label}</Link>
            )}
            <div className="flex items-center gap-3 pt-4 pb-2">
              <Link to="/login" onClick={() => setOpen(false)} className="flex-1 text-center rounded-full border border-sand-200 text-ink font-semibold px-5 py-3 hover:border-ink transition-colors">Sign in</Link>
              <a href="/#contact" onClick={() => setOpen(false)} className="flex-1 text-center rounded-full bg-redline-500 text-white font-semibold px-5 py-3 hover:bg-redline-600 transition-colors">Book a demo</a>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}

export function MarketingCTA({ title = "See it answering your residents.", sub = "We provision your line, digitize your documents and load your roster, most communities are live in about a week." }) {
  return (
    <section className="max-w-6xl mx-auto px-6 pb-24" data-testid="marketing-cta">
      <div className="relative overflow-hidden rounded-3xl bg-ink text-white p-10 sm:p-14 text-center">
        <div className="absolute -left-16 -bottom-16 h-56 w-56 rounded-full bg-redline-500/20 blur-3xl" />
        <div className="relative max-w-2xl mx-auto">
          <h2 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight">{title}</h2>
          <p className="mt-4 text-sand-300 leading-relaxed">{sub}</p>
          <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
            <a href="/#contact" data-testid="marketing-cta-demo" className="inline-flex items-center gap-2 rounded-full bg-redline-500 text-white font-semibold px-7 py-3.5 hover:bg-redline-600 transition-colors">
              Book a demo <ArrowRight className="h-4 w-4" />
            </a>
            <Link to="/solutions" className="inline-flex items-center gap-2 rounded-full bg-white/10 text-white font-semibold px-7 py-3.5 hover:bg-white/15 transition-colors">
              Explore solutions
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

export function MarketingFooter() {
  const cols = [
    { h: "Solutions", links: [
      ["/solutions/hoa-communication-software", "HOA communication software"],
      ["/field-services", "Vendors & work orders"],
      ["/solutions/ai-answering-service-property-management", "AI answering service"],
      ["/solutions/self-managed-hoa-software", "Self-managed HOAs"],
      ["/solutions/condo-association-software", "Condo associations"],
      ["/solutions/hoa-management-companies", "Management companies"],
    ] },
    { h: "Compare", links: [
      ["/compare/townsq-alternative", "TownSq alternative"],
      ["/compare/vantaca-alternative", "Vantaca alternative"],
      ["/compare/cinc-systems-alternative", "CINC alternative"],
      ["/compare/payhoa-alternative", "PayHOA alternative"],
      ["/compare/frontsteps-alternative", "FrontSteps alternative"],
    ] },
    { h: "Resources", links: [
      ["/resources", "Guides & playbooks"],
      ["/playbook", "Modern management playbook"],
      ["/resources/how-to-choose-hoa-management-software", "HOA software buyer's guide"],
      ["/resources/hoa-texting-tcpa-a2p-10dlc-compliance", "HOA texting compliance"],
      ["/resources/how-to-reduce-hoa-resident-complaints", "Reduce resident complaints"],
      ["/faq", "FAQ"],
    ] },
    { h: "Company", links: [
      ["/about", "About us"],
      ["/careers", "Careers"],
      ["/security", "Security"],
      ["/pricing", "Pricing"],
    ] },
  ];
  return (
    <footer className="bg-ink text-sand-300 pt-16 pb-10" data-testid="marketing-footer">
      <div className="max-w-6xl mx-auto px-6">
        <div className="grid gap-10 sm:grid-cols-2 lg:grid-cols-5">
          <div>
            <div className="flex items-center gap-2 text-white">
              <div className="h-8 w-8 rounded-lg bg-redline-500 flex items-center justify-center shrink-0"><MessageSquareText className="h-4 w-4" strokeWidth={1.5} /></div>
              <span className="font-heading font-bold">Ask Redline®</span>
            </div>
            <p className="mt-4 text-sm leading-relaxed text-sand-400 max-w-xs">The AI operating platform for HOAs and community associations, a text &amp; call resident concierge, a manager copilot, and the intelligence to run every community.</p>
          </div>
          {cols.map((c) => (
            <div key={c.h}>
              <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-sand-500 mb-4">{c.h}</div>
              <ul className="space-y-2.5 text-sm">
                {c.links.map(([to, label]) => (
                  <li key={to}>
                    {to.startsWith("/#")
                      ? <a href={to} className="hover:text-white transition-colors">{label}</a>
                      : <Link to={to} className="hover:text-white transition-colors">{label}</Link>}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        <div className="mt-12 pt-8 border-t border-white/10 flex flex-col sm:flex-row items-center justify-between gap-4 text-sm">
          <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2">
            <Link to="/opt-in" className="hover:text-white transition-colors">SMS Opt-In</Link>
            <Link to="/privacy" className="hover:text-white transition-colors">Privacy Policy</Link>
            <Link to="/terms" className="hover:text-white transition-colors">Terms of Service</Link>
            <Link to="/dpa" className="hover:text-white transition-colors">Data Processing Addendum</Link>
          </div>
          <span className="whitespace-nowrap text-sand-400">© {new Date().getFullYear()} Ask Redline · Austin, TX</span>
        </div>
      </div>
    </footer>
  );
}
